import * as R from 'remeda';
import { createSelector } from 'reselect';
import { aggregatedSelectors } from 'store/aggregated';
import { actionSelectors } from 'store/data/action';
import { suggestionSelectors } from 'store/data/suggestion';
import Types from 'Types';
import { timeUtility } from 'utility';
import { misc } from 'variables';

import { improvementsConstants } from '.';
const domain = (state: Types.RootState) => state.page.improvements;
// Sub-domain accessor for the actionboard slice so the actionboard selectors
// stay narrow and don't pull the whole improvements state on every memo check.
const actionboardDomain = createSelector(
  domain,
  (domain) => domain.actionboard,
);

const activeTabId = createSelector(domain, (domain) => domain.activeTabId);

const selectedSuggestionId = createSelector(
  domain,
  (domain) => domain.selectedSuggestionId,
);

const selectedActionId = createSelector(
  domain,
  (domain) => domain.selectedActionId,
);

// Filter-chip selection. Shared across the Suggestions and Actions tabs.
const selectedAreaIds = createSelector(
  domain,
  (domain) => domain.selectedAreaIds,
);

// Helper: does this entity's improvementAreaId pass the chip filter?
// Empty selection means "show all". A null areaId is matched by the
// UNASSIGNED_AREA_ID sentinel.
const passesAreaFilter = (
  areaId: string | null,
  selected: string[],
): boolean => {
  if (selected.length === 0) return true;
  const key = areaId ?? improvementsConstants.UNASSIGNED_AREA_ID;
  return selected.includes(key);
};

const suggestionTableRows = createSelector(
  suggestionSelectors.list,
  selectedAreaIds,
  (suggestions, selected) => {
    // suggestionSelectors.list already returns newest-`created` first
    // (sortBy created desc lives in the data domain). Filter without
    // re-sorting so the existing ordering carries through.
    const tableRows: PivotTable.Row[] = R.pipe(
      suggestions,
      R.filter((suggestion) =>
        passesAreaFilter(suggestion.improvementAreaId, selected),
      ),
      R.map((suggestion) => {
        const row: PivotTable.Row = {
          id: suggestion.id,
          name: suggestion.name,
          ancestorIds: [],
          indentLevel: 0,
        };

        return row;
      }),
    );
    return tableRows;
  },
);

// Composable sort key for endAt — `Date.UTC(year, monthIndex, day)` returns
// epoch millis without involving the Time.Hour padding the API transformer
// applies, so two actions with the same day always tie regardless of hour.
// DateItem's month is 1-indexed (Jan = 1), so subtract one for Date.UTC.
const endAtSortKey = (action: Data.Action): number =>
  Date.UTC(action.endAt.year, action.endAt.month - 1, action.endAt.dayOfMonth);

const actionTableRows = createSelector(
  actionSelectors.list,
  selectedAreaIds,
  (actions, selected) => {
    const tableRows: PivotTable.Row[] = R.pipe(
      actions,
      R.filter((action) =>
        passesAreaFilter(action.improvementAreaId, selected),
      ),
      // Latest deadline first — newest endAt at the top of the list.
      R.sortBy([endAtSortKey, 'desc']),
      R.map((action) => {
        // Deadline rides on the row as `subtitle` (rendered under the name) so
        // the actions table can drop its own Deadline column and free width for
        // the Responsible column.
        //
        // Spread `endAt` FIRST and then force `type: 'day'`. The API transformer
        // (`toAction`) builds endAt via `toTimeDate` which returns a Time.Hour
        // shape (`type: 'hour'`) — leading with `type: 'day'` lets the spread
        // overwrite it and the formatter falls through to `MISSING`.
        const formattedDeadline = timeUtility.format(
          { ...action.endAt, type: 'day' },
          'actions-table',
        );
        const row: PivotTable.Row = {
          id: action.id,
          name: action.name,
          subtitle:
            formattedDeadline === misc.MISSING ? '-' : formattedDeadline,
          ancestorIds: [],
          indentLevel: 0,
        };

        return row;
      }),
    );
    return tableRows;
  },
);
const hasMockedSuggestion = createSelector(suggestionSelectors.byId, (byId) =>
  R.pipe(byId, R.prop(improvementsConstants.MOCKED_SUGGETION_ID), R.isDefined),
);

const hasMockedAction = createSelector(actionSelectors.byId, (byId) =>
  R.pipe(byId, R.prop(improvementsConstants.MOCKED_ACTION_ID), R.isDefined),
);

// ==========================
// Actionboard (sub-view) / Selectors
// ==========================
// `actionboardSelectedWeek` returns a Time.LabeledDate so the UI can show a
// human-readable week label in the stepper header. Format key was renamed
// from 'actionboard-selected-week' to 'improvements-selected-week' on the
// move into this domain.
const actionboardSelectedWeek = createSelector(actionboardDomain, (sub) => {
  const label = timeUtility.format(
    sub.selectedWeek,
    'improvements-selected-week',
  );
  const labeledDate: Time.LabeledDate = { ...sub.selectedWeek, name: label };
  return labeledDate;
});

const actionboardSelectedEntityId = createSelector(
  actionboardDomain,
  (sub) => sub.selectedEntityId,
);

// Composes aggregated.kpi using the actionboard's week + entity selection.
// `entityIds` is empty when the user has chosen the warehouse-wide view
// (selectedEntityId === null) so the KPI aggregates across all entities.
const actionboardGoalKPIs = createSelector(
  (state: Types.RootState) => state,
  actionboardSelectedWeek,
  actionboardSelectedEntityId,
  (state, week, entityId) =>
    aggregatedSelectors.kpi(state, {
      dateId: week.id,
      entityIds: entityId ? [entityId] : [],
    }),
);

export {
  actionboardGoalKPIs,
  actionboardSelectedEntityId,
  actionboardSelectedWeek,
  actionTableRows,
  activeTabId,
  hasMockedAction,
  hasMockedSuggestion,
  selectedActionId,
  selectedAreaIds,
  selectedSuggestionId,
  suggestionTableRows,
};
